// Protects a running recording against leaving the page: in-app links ask first (custom dialog),
// browser back / typed URLs are intercepted on `hashchange`, reloads/closing get `beforeunload`.
import { useEffect, useRef, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { navigate, routeToHash, type Route } from '../lib/router';
import { confirmDialog } from './Dialog';
import { toast } from './Toasts';

interface Guard {
  message: string;
  /** Stops and saves the recording; called once the user agreed to leave. */
  onLeave: () => Promise<boolean>;
  /** Hash of the page that registered the guard. */
  hash: string;
  /** Hashes that keep the guarded page mounted (e.g. expanding a note) are not intercepted. */
  allowHash?: (hash: string) => boolean;
}

function isAllowed(g: Guard, hash: string): boolean {
  return hash === g.hash || (g.allowHash?.(hash) ?? false);
}

let guard: Guard | null = null;
let installed = false;
const guardListeners = new Set<() => void>();

function setGuard(next: Guard | null): void {
  if (guard === next) return;
  guard = next;
  for (const listener of [...guardListeners]) listener();
}

function subscribeGuard(listener: () => void): () => void {
  guardListeners.add(listener);
  return () => {
    guardListeners.delete(listener);
  };
}

const hasGuard = () => guard !== null;

/**
 * True while a page records (its leave guard is active): that page must stay mounted, e.g. under
 * the login when the session ends meanwhile.
 */
export function useRecordingActive(): boolean {
  return useSyncExternalStore(subscribeGuard, hasGuard, hasGuard);
}
/** Set while we re-dispatch an approved navigation, so it is not intercepted again. */
let passThrough = false;

const DEFAULT_MESSAGE = 'Snemanje še poteka. Ga želite ustaviti, shraniti posnetek in zapustiti stran?';

/** Asks the user (if a recording is running) and saves it. Resolves true when leaving is fine. */
export async function confirmLeave(targetHash?: string): Promise<boolean> {
  const g = guard;
  if (!g || (targetHash !== undefined && isAllowed(g, targetHash))) return true;
  const ok = await confirmDialog({
    title: 'Snemanje še poteka',
    message: g.message,
    confirmLabel: 'Ustavi in shrani',
    cancelLabel: 'Ostani na strani',
  });
  if (!ok) return false;
  let saved = false;
  try {
    saved = await g.onLeave();
  } catch (err) {
    console.error('[nav] saving before leaving failed', err);
  }
  if (!saved) {
    toast('Posnetka ni bilo mogoče shraniti, zato ostajate na strani.', { kind: 'error' });
    return false;
  }
  if (guard === g) setGuard(null);
  return true;
}

/** navigate() that respects a running recording. */
export async function requestNavigate(route: Route, opts?: { replace?: boolean }): Promise<void> {
  if (await confirmLeave(routeToHash(route))) navigate(route, opts);
}

/**
 * Registers the hashchange interceptor. Must run before React mounts so it is the first listener
 * (useRoute() listens on the same event); it also uses the capture phase for the same reason.
 */
export function installNavigationGuard(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener(
    'hashchange',
    (event) => {
      const g = guard;
      if (passThrough || !g || isAllowed(g, window.location.hash)) return;
      // Keep the page (and its recorder) mounted while we ask.
      event.stopImmediatePropagation();
      const target = window.location.hash;
      history.replaceState(null, '', g.hash);
      void confirmLeave().then((ok) => {
        if (!ok) return;
        history.replaceState(null, '', target);
        passThrough = true;
        try {
          window.dispatchEvent(new HashChangeEvent('hashchange'));
        } finally {
          passThrough = false;
        }
      });
    },
    { capture: true },
  );
}

export interface LeaveGuardOptions {
  message?: string;
  /** Hashes that keep the page mounted; must be a stable function (module level). */
  allowHash?: (hash: string) => boolean;
}

/** While `active`, leaving the page asks first and saves via `onLeave` (returns success). */
export function useLeaveGuard(active: boolean, onLeave: () => Promise<boolean>, opts: LeaveGuardOptions = {}): void {
  const { message = DEFAULT_MESSAGE, allowHash } = opts;
  const onLeaveRef = useRef(onLeave);
  useEffect(() => {
    onLeaveRef.current = onLeave;
  });

  useEffect(() => {
    if (!active) return;
    const g: Guard = { message, onLeave: () => onLeaveRef.current(), hash: window.location.hash };
    if (allowHash) g.allowHash = allowHash;
    setGuard(g);
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers need returnValue set to show the prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      if (guard === g) setGuard(null);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [active, message, allowHash]);
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: Route;
  children: ReactNode;
}

/** Hash link that asks before leaving a running recording. */
export function Link({ to, onClick, children, ...rest }: LinkProps) {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || !guard || isAllowed(guard, routeToHash(to))) return;
    // Modifier clicks open a new tab: nothing to protect in this one.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    void requestNavigate(to);
  };
  return (
    <a href={routeToHash(to)} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
